/**
 * useShopConfig — the single entry point for fetching shop config and shop
 * details for a given `shopId`.
 *
 * Responsibilities:
 *  - Read the per-shop entries from Redux (`configByShopId`, `shopDetailsByShopId`).
 *  - Fire the two axios calls exactly once per shopId per session (subject to
 *    an explicit `refetch()`), using a module-scope in-flight Map so multiple
 *    components mounting in the same tick can't double-fire.
 *  - Never wipe cached data on failure — errors just set the `error` field.
 *  - Race-safe: results are committed keyed by the captured shopId, so an
 *    out-of-order resolution for shop A can never poison shop B's entry.
 *  - Gated on `isShopUser(role)` — no-ops for management roles.
 *
 * Consumers use the returned tuple:
 *   const { config, shopDetails, loading, error, refetch } = useShopConfig(shopId);
 */

import { useCallback, useEffect } from "react";
import type { Dispatch } from "redux";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react";

import fetchShopConfigService from "../services/fetchShopConfig";
import shopDetailsService from "../services/fetchShopDetails";
import {
  setShopConfigLoading,
  setShopConfigSuccess,
  setShopConfigError,
  setShopDetailsLoading,
  setShopDetailsSuccess,
  setShopDetailsError,
} from "../store/allOrders.slice";
import {
  selectShopConfigEntry,
  selectShopDetailsEntry,
} from "../store/allOrders.selectors";
import type { AsyncEntry, ShopConfig, ShopDetails } from "../types/shopConfig";
import { isShopUser } from "../utils/roles";
import { getSession } from "../utils/session";

// -----------------------------------------------------------------------------
// TTL
// -----------------------------------------------------------------------------
// Cached shop config / shop details are considered fresh for this long.
// Beyond it, `useShopConfig` treats the entry as no-data and refetches so the
// UI never renders stale shop-scoped data.

const CACHE_TTL_MS = 8 * 60 * 60 * 1000; // 8 hours

const isFresh = <T,>(entry: AsyncEntry<T> | undefined): boolean =>
  Boolean(
    entry?.data &&
      entry?.fetchedAt &&
      Date.now() - entry.fetchedAt < CACHE_TTL_MS,
  );

// -----------------------------------------------------------------------------
// Module-scope in-flight caches
// -----------------------------------------------------------------------------
// A single Redux dispatch tick still allows two components rendering in the
// same commit to both see `loading: false` and both call the fetch helper.
// The in-flight Map dedupes at the promise level.

const inflightConfig = new Map<string, Promise<void>>();
const inflightDetails = new Map<string, Promise<void>>();

interface FetchOpts {
  force?: boolean;
}

/**
 * Fire the shop-config fetch for `shopId`. Idempotent unless `force` is set.
 * Dispatches loading -> success/error reducers keyed by the captured shopId.
 */
export function fetchConfig(
  shopId: string,
  dispatch: Dispatch,
  opts: FetchOpts = {},
): Promise<void> {
  if (!opts.force) {
    const existing = inflightConfig.get(shopId);
    if (existing) return existing;
  }

  dispatch(setShopConfigLoading({ shopId }));

  const promise = fetchShopConfigService
    .fetchShopConfig(shopId)
    .then((res) => {
      const data: ShopConfig | null = res?.data?.result?.data ?? null;
      dispatch(setShopConfigSuccess({ shopId, data }));
    })
    .catch((err) => {
      Sentry.captureException(err, {
        tags: { feature: "shop_config_fetch" },
        extra: { shopId },
      });
      const message =
        (err instanceof Error && err.message) ||
        (typeof err === "string" ? err : "shop_config_fetch_failed");
      dispatch(setShopConfigError({ shopId, error: message }));
    })
    .finally(() => {
      inflightConfig.delete(shopId);
    });

  inflightConfig.set(shopId, promise);
  return promise;
}

/**
 * Fire the shop-details fetch for `shopId`. Idempotent unless `force` is set.
 */
export function fetchDetails(
  shopId: string,
  dispatch: Dispatch,
  opts: FetchOpts = {},
): Promise<void> {
  if (!opts.force) {
    const existing = inflightDetails.get(shopId);
    if (existing) return existing;
  }

  dispatch(setShopDetailsLoading({ shopId }));

  const promise = shopDetailsService
    .getShopDetails(shopId)
    .then((res) => {
      const data: ShopDetails | null = res?.data?.result?.data ?? null;
      dispatch(setShopDetailsSuccess({ shopId, data }));
    })
    .catch((err) => {
      Sentry.captureException(err, {
        tags: { feature: "shop_details_fetch" },
        extra: { shopId },
      });
      const message =
        (err instanceof Error && err.message) ||
        (typeof err === "string" ? err : "shop_details_fetch_failed");
      dispatch(setShopDetailsError({ shopId, error: message }));
    })
    .finally(() => {
      inflightDetails.delete(shopId);
    });

  inflightDetails.set(shopId, promise);
  return promise;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export interface UseShopConfigResult {
  config: ShopConfig | null;
  shopDetails: ShopDetails | null;
  /**
   * True when either resource is loading AND its cached data is not yet
   * available. Once cached data exists a background refetch does not
   * flip this back to true — the UI keeps rendering the stale-but-valid
   * copy.
   */
  loading: boolean;
  /**
   * Non-null when at least one resource errored AND has no cached data
   * to fall back on. If cached data exists this stays null (the UI
   * prefers stale-good data over showing an error).
   */
  error: string | null;
  /** Force a re-fetch of both resources for the current shopId. */
  refetch: () => void;
}

export function useShopConfig(shopId: string | null): UseShopConfigResult {
  const dispatch = useDispatch();

  // Read role lazily. `getSession` reads localStorage on every call, so keep
  // it inside effects/callbacks instead of triggering re-renders.
  const configEntry = useSelector((s: Parameters<typeof selectShopConfigEntry>[0]) =>
    selectShopConfigEntry(s, shopId),
  );
  const detailsEntry = useSelector((s: Parameters<typeof selectShopDetailsEntry>[0]) =>
    selectShopDetailsEntry(s, shopId),
  );

  useEffect(() => {
    if (!shopId) return;
    const role = getSession("role");
    if (!isShopUser(role)) return;

    // Fetch trigger is data-plus-freshness. Stale entries (older than
    // CACHE_TTL_MS) are treated as no-data so a fresh fetch fires. Dedup
    // lives in the module-scope in-flight Map so same-tick double-fires
    // still share one network call.
    if (!isFresh(configEntry)) {
      void fetchConfig(shopId, dispatch);
    }
    if (!isFresh(detailsEntry)) {
      void fetchDetails(shopId, dispatch);
    }
  }, [
    shopId,
    dispatch,
    configEntry?.data,
    configEntry?.fetchedAt,
    detailsEntry?.data,
    detailsEntry?.fetchedAt,
  ]);

  const refetch = useCallback(() => {
    if (!shopId) return;
    const role = getSession("role");
    if (!isShopUser(role)) return;
    void fetchConfig(shopId, dispatch, { force: true });
    void fetchDetails(shopId, dispatch, { force: true });
  }, [shopId, dispatch]);

  // Only surface data if the cached entry is still within TTL. A stale entry
  // reads as `null` so ShopDataGate keeps the loader up until the fresh
  // fetch (triggered by the effect above) settles.
  const config = isFresh(configEntry) ? configEntry!.data : null;
  const shopDetails = isFresh(detailsEntry) ? detailsEntry!.data : null;

  const loading = Boolean(
    (configEntry?.loading && !config) ||
      (detailsEntry?.loading && !shopDetails),
  );

  const error =
    (!config && configEntry?.error) ||
    (!shopDetails && detailsEntry?.error) ||
    null;

  return { config, shopDetails, loading, error, refetch };
}

export default useShopConfig;
